import { useState } from 'react';
import { QuickstartTheme, getThemePrimaryTextColor } from '../../../features/quickstart/types/theme';
import { Button } from '@playcastdotio/Shadcn';
import { useIsMobileDevice } from '../../../utility/utility';
import { QuickstartCard } from '../../../features/quickstart/components/QuickstartCard';
import { ANIMATION_PRESETS } from '../../../features/quickstart/config/animations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKeyboard as faKeyboardDuotone } from '@fortawesome/duotone-light-svg-icons/faKeyboard';
import { faMouse as faMouseDuotone } from '@fortawesome/duotone-light-svg-icons/faMouse';
import { faGamepadModern as faGamepadDuotone } from '@fortawesome/duotone-light-svg-icons/faGamepadModern';

declare global {
  interface Navigator {
    keyboard: {
      lock: () => Promise<undefined>;
    };
  }
}

interface PlayNowOverlayProps {
  theme: QuickstartTheme;
  onStart: () => void;
  requestBrowserLocks?: () => Promise<void>;
  remainingSeconds?: number;
  isResuming?: boolean;
}

export const PlayNowOverlay = ({ theme, onStart, requestBrowserLocks, remainingSeconds, isResuming }: PlayNowOverlayProps) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const handlePlayNow = async () => {
    if (isRequesting) return;
    setIsRequesting(true);

    // CRITICAL (Android fix): Use the user gesture to play video BEFORE fullscreen consumes it
    // On Android, requestFullscreen() pauses the video as part of the fullscreen transition.
    // By calling play() first with the user gesture, we ensure the video can resume itself.
    // Without this, the video stays paused with NotAllowedError (no gesture available).
    const videoElement = document.querySelector('video');
    if (videoElement) {
      console.log('[PlayNowOverlay] Calling video.play() with user gesture before fullscreen');
      videoElement.play().catch(err => {
        console.warn('[PlayNowOverlay] Video play pre-fullscreen failed:', err);
      });
    }

    try {
      // Request browser locks (fullscreen, pointer lock, keyboard lock)
      // This is now handled by the controller for better architecture
      // NOTE: This is non-blocking - we proceed even if locks fail (graceful degradation)
      if (requestBrowserLocks) {
        await requestBrowserLocks();
      }
    } catch (error) {
      console.warn('[PlayNowOverlay] Browser locks not available, continuing anyway:', error);
      // Continue without locks - this is expected on some mobile browsers
    }

    // Always notify parent to start playing, even if locks failed
    onStart();
  };

  // Reactive mobile detection - updates when viewport changes (e.g., "Request Desktop Site")
  const isMobile = useIsMobileDevice();

  // Create a custom theme for PlayNow overlay if playNowModalBackgroundColor is set
  const playNowTheme = {
    ...theme,
    modalBackgroundColor: theme.playNowModalBackgroundColor || theme.modalBackgroundColor,
    modalBackgroundOpacity: theme.playNowModalBackgroundOpacity ?? theme.modalBackgroundOpacity,
  };

  return (
    <div className='pointer-events-none absolute inset-0 z-[100] flex items-center justify-center'>
      <div className='pointer-events-auto'>
        <QuickstartCard theme={playNowTheme} animation={ANIMATION_PRESETS.cardDefault} showHeader={false} hideFooter>
          <div className='flex flex-col items-center text-center'>
            {/* Game Logo */}
            {theme.gameLogo && <img src={theme.gameLogo} alt={theme.gameName} className='mb-8 h-20' />}

            {/* Your Controls Text */}
            <p className='text-lg font-semibold' style={{ color: theme.primaryColor }}>
              Your Controls:
            </p>

            {/* Control Icons */}
            <div className='mb-8 flex items-center justify-center gap-4'>
              {/* Keyboard + Mouse Icon (combined) - Desktop only */}
              {!isMobile && (
                <div className='relative flex items-center justify-center'>
                  <FontAwesomeIcon
                    icon={faKeyboardDuotone}
                    className='text-darkest text-5xl'
                    style={
                      {
                        '--fa-secondary-color': '#22C55E',
                        '--fa-secondary-opacity': '1',
                        strokeWidth: '4.5px',
                        stroke: '#020617',
                      } as React.CSSProperties
                    }
                  />
                  <FontAwesomeIcon
                    icon={faMouseDuotone}
                    className='text-darkest absolute bottom-0 right-0 text-3xl'
                    style={
                      {
                        '--fa-secondary-color': '#22C55E',
                        '--fa-secondary-opacity': '1',
                        strokeWidth: '4.5px',
                        stroke: '#020617',
                      } as React.CSSProperties
                    }
                  />
                </div>
              )}

              {/* Gamepad Icon */}
              <FontAwesomeIcon
                icon={faGamepadDuotone}
                className='text-[2.7rem]'
                style={
                  {
                    '--fa-secondary-color': '#22C55E',
                    '--fa-secondary-opacity': '1',
                    strokeWidth: '4.5px',
                    stroke: '#020617',
                  } as React.CSSProperties
                }
              />
            </div>

            {/* Join Button */}
            <Button
              onClick={handlePlayNow}
              disabled={isRequesting}
              style={{
                backgroundColor: theme.primaryColor,
                color: getThemePrimaryTextColor(theme),
              }}
              className='rounded-full border-2 border-black px-8 py-4 text-lg font-bold shadow-lg hover:opacity-90'
              size='lg'
            >
              {isRequesting ? 'Starting...' : isMobile ? 'Tap Here To Join' : 'Click Here To Join'}
            </Button>
          </div>
        </QuickstartCard>

        {/* Timer below card - Always shows AFK timer */}
        {/* {remainingSeconds !== undefined && (
          <div className='mt-4 flex w-full justify-center'>
            <div
              className='rounded-lg px-8 py-3 text-center backdrop-blur-sm'
              style={{
                backgroundColor: getBackgroundColorWithOpacity(theme.modalBackgroundColor, theme.modalBackgroundOpacity ?? 0.95),
              }}
            >
            <p className='text-xs font-semibold text-white/80'>
              {isResuming ? 'Auto-kick in' : 'Auto-kick in'}
            </p>
            <p className='text-3xl font-bold' style={{ color: theme.primaryColor }}>
              {formatTime(remainingSeconds)}
            </p>
            <p className='mt-1 text-xs text-white/60'>
              {isResuming ? 'Click Play Now to resume' : 'Click Play Now to start'}
            </p>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

